import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  SubscriptionTimeInForceModal,
  SubscriptionTimeInForceModalProps,
} from '@pages/Subscriptions/modals/SubscriptionTimeInForceType.modal'
import { UnsubscribeAttentionModal } from '@pages/Subscriptions/modals/UnsubscribeAttention.modal'
import { UnsubscribeConfirmationModal } from '@pages/Subscriptions/modals/UnsubscribeConfirmation.modal'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { InfoIcon } from '@ui/icons/Info.icon'

import { formatDate, isLastBusinessDay } from '@utils/date.utils'

import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import {
  Subscription,
  SubscriptionDetail,
  isCancellationRequested,
  isSubscriptionActive,
  isSubscriptionDeductingFundOrInProcess,
  isSubscriptionImmediateAndPending,
  isSubscriptionPending,
} from '@model/SubscriptionTypes'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { CancelOrProcessingButton } from './ProductActions'
import { SubscriptionDetailMessage } from './SubscriptionDetailMessage'
import { ConfirmCancelSubscriptionModal } from './modals/ConfirmCancelSubscription.modal'
import { PendingUnsubscriptionModal } from './modals/PendingUnsubscription.modal'
import { ResubscribeModal } from './modals/Resubscribe.modal'
import { SubscriptionDetailModal } from './modals/SubscriptionDetail.modal'
import {
  allowSubDowngrade,
  allowSubUpgrade,
  getActiveSubscriptionLevel,
  getNextSubLevel,
  getPendingCancelSubscriptionLevel,
  getPendingUpdateSubscriptionLevel,
  getPreviousSubLevel,
  getSubscriptionDefaultLevel,
  hasActivatingSubscription,
  hasInProcessDowngrade,
  hasInProcessUpgrade,
  hasLevelInCart,
  hasPendingDowngrade,
  hasPendingUnsubscriptionRequest,
  hasPendingUpgrade,
  isLevelDowngrade,
} from './subscriptionUtils'

interface TimeInForceContext {
  subscription: Subscription
  subscriptionDetail: SubscriptionDetail
}

const renderNewSubscriptionPending = (
  amountPerMonth: string,
  dueDate: string,
  t: TFunction,
  dateFormat?: string
) => (
  <div>
    <p>{amountPerMonth}</p>
    <p className='has-text-warning'>
      <Body2>
        {t('Subscriptions.Starting', {
          firstDateOfNextMonth: formatDate(dueDate, { formatType: dateFormat }),
        })}
      </Body2>
    </p>
  </div>
)

const renderCancelExistingSubscription = (
  amountPerMonth: string,
  dueDate: string,
  setPendingUnsubscriptionInfoModal: (value: boolean) => void,
  t: TFunction,
  dateFormat?: string
) => {
  return (
    <div>
      <span>{amountPerMonth}</span>
      <Body2 className='pl-2 has-text-success'>
        {t('Subscriptions.Active until', {
          date: formatDate(dueDate, { formatType: dateFormat }),
        })}
      </Body2>
      <div className='is-flex mt-1'>
        <Body2 className='has-text-warning'>{t('Subscriptions.Pending Unsubscription')} </Body2>
        <div className='ml-2 pt-1'>
          <ActionIcon
            icon={InfoIcon}
            size='x20'
            onClick={() => {
              setPendingUnsubscriptionInfoModal(true)
            }}
          />
        </div>
      </div>
    </div>
  )
}

interface SubscriptionGroupSubscriptionProps {
  subscription: Subscription
  isSubscribedToBundle: boolean
  onAddCartItem(
    subscriptionDetailId: string,
    subscriptionRequestTypeId: SubscriptionRequestType
  ): void
  onUpdateSubscription(data: UpdateSubscriptionRequest, selectedItem?: SubscriptionDetail): void
  isCollapsed: boolean
  scrollToCart?(): void
  nonActiveSubscriptions: SubscriptionCartItem[]
}

interface SubscriptionGroupProductPriceProps extends SubscriptionGroupSubscriptionProps {
  subscription: Subscription
  nonActiveSubscriptions: SubscriptionCartItem[]
}

interface SubscriptionGroupDetailProps {
  subscription: Subscription
  selectedSubscriptionDetail?: SubscriptionDetail
  handleOnSubscriptionDetailChange(subscriptionDetail: SubscriptionDetail): void
  handleOnSetConfirmCancelSubscriptionModal: (subscriptionDetail?: SubscriptionDetail) => void
  nonActiveSubscriptions: SubscriptionCartItem[]
  setPendingUnsubscriptionInfoModal(visible: boolean): void
}

const SubscriptionGroupDetail: React.FC<SubscriptionGroupDetailProps> = ({
  subscription,
  selectedSubscriptionDetail,
  handleOnSubscriptionDetailChange,
  handleOnSetConfirmCancelSubscriptionModal,
  setPendingUnsubscriptionInfoModal,
  nonActiveSubscriptions,
}) => {
  const { t } = useTranslation()

  const detailModal = useModal(SubscriptionDetailModal)
  const isLevelSelectionDisabled = useMemo(
    () =>
      hasPendingUnsubscriptionRequest(subscription) ||
      hasActivatingSubscription(subscription) ||
      hasLevelInCart(subscription, nonActiveSubscriptions),
    [subscription, nonActiveSubscriptions]
  )
  const pendingUpgrade = useMemo(
    () => hasPendingUpgrade(subscription, selectedSubscriptionDetail),
    [subscription, selectedSubscriptionDetail]
  )
  const pendingDowngrade = useMemo(
    () => hasPendingDowngrade(subscription, selectedSubscriptionDetail),
    [subscription, selectedSubscriptionDetail]
  )

  const allowUpgrade = useMemo(
    () => !isLevelSelectionDisabled && allowSubUpgrade(subscription, selectedSubscriptionDetail),
    [isLevelSelectionDisabled, subscription, selectedSubscriptionDetail]
  )

  const allowDowngrade = useMemo(
    () => !isLevelSelectionDisabled && allowSubDowngrade(subscription, selectedSubscriptionDetail),
    [isLevelSelectionDisabled, subscription, selectedSubscriptionDetail]
  )

  const inProcessUpgrade = useMemo(
    () => hasInProcessUpgrade(subscription, selectedSubscriptionDetail),
    [subscription, selectedSubscriptionDetail]
  )

  const inProcessDowngrade = useMemo(
    () => hasInProcessDowngrade(subscription, selectedSubscriptionDetail),
    [subscription, selectedSubscriptionDetail]
  )

  return (
    <>
      {selectedSubscriptionDetail && (
        <Body2>
          {`${selectedSubscriptionDetail.name} - $${selectedSubscriptionDetail.amount}/${t(
            'Subscriptions.mth'
          )}`}
        </Body2>
      )}
      {((allowUpgrade && !pendingUpgrade) || pendingDowngrade || inProcessDowngrade) && (
        <Button.Ghost
          size='sm'
          disabled={pendingDowngrade || inProcessDowngrade}
          onClick={() =>
            pendingDowngrade
              ? handleOnSetConfirmCancelSubscriptionModal(selectedSubscriptionDetail)
              : detailModal.open({
                  subscription,
                  selectedSubscriptionDetail: getNextSubLevel(
                    subscription,
                    selectedSubscriptionDetail
                  ),
                  isSelectEnabled: false,
                  onConfirm: (detail) => {
                    if (detail) handleOnSubscriptionDetailChange(detail)
                  },
                })
          }
        >
          {t('Upgrade')}
        </Button.Ghost>
      )}
      {((allowDowngrade && !pendingDowngrade) || pendingUpgrade || inProcessUpgrade) && (
        <Button.Ghost
          size='sm'
          disabled={
            pendingUpgrade ||
            inProcessUpgrade ||
            !!selectedSubscriptionDetail?.excludedBySubscriptionDetailId
          }
          onClick={() =>
            pendingUpgrade
              ? handleOnSetConfirmCancelSubscriptionModal(selectedSubscriptionDetail)
              : detailModal.open({
                  subscription,
                  selectedSubscriptionDetail: getPreviousSubLevel(
                    subscription,
                    selectedSubscriptionDetail
                  ),
                  isSelectEnabled: false,
                  onConfirm: (detail) => {
                    if (detail) handleOnSubscriptionDetailChange(detail)
                  },
                })
          }
        >
          {t('Downgrade')}
        </Button.Ghost>
      )}
      <SubscriptionDetailMessage
        selectedSubscription={subscription}
        onSetPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
      />
    </>
  )
}

export const useModals = () => {
  const [selectedSubscriptionDetail, setSelectedSubscriptionDetail] = useState<SubscriptionDetail>()
  const [previousSelectedSubscriptionDetail, setPreviousSelectedSubscriptionDetail] =
    useState<SubscriptionDetail>()
  const [subscriptionTimeInForceType, setSubscriptionTimeInForceType] = useState<
    SubscriptionTimeInForce | undefined
  >()

  return {
    selectedSubscriptionDetail,
    setSelectedSubscriptionDetail,
    previousSelectedSubscriptionDetail,
    setPreviousSelectedSubscriptionDetail,
    subscriptionTimeInForceType,
    setSubscriptionTimeInForceType,
  }
}

export const SubscriptionGroupProductPrice: React.FC<
  SubscriptionGroupProductPriceProps & ReturnType<typeof useModals>
> = (props) => {
  const { subscription, nonActiveSubscriptions, onUpdateSubscription, onAddCartItem, ...modals } =
    props
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatMoney } = useFormatNumber()

  const pendingUnsubscriptionModal = useModal(PendingUnsubscriptionModal)
  const cancelPendingModal = useModal(ConfirmCancelSubscriptionModal, {
    onConfirm: () => handleOnCancelPendingSubscription(modals.selectedSubscriptionDetail?.id),
  })
  const timeInForceModal = useModal(SubscriptionTimeInForceModal)

  const setPendingUnsubscriptionInfoModal = (visible: boolean) => {
    if (!visible) return
    const dueDate = modals.selectedSubscriptionDetail?.subscriptionRequest?.dueDate
    pendingUnsubscriptionModal.open({ dueDate })
  }

  const handleOnCancelPendingSubscription = (subscriptionDetailId?: string) => {
    onUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: subscriptionDetailId || modals.selectedSubscriptionDetail?.id,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
      overrideOpenPositionsForCancel: true,
    })
  }

  const handleOnSetConfirmCancelSubscriptionModal = (subscriptionDetail?: SubscriptionDetail) => {
    if (subscriptionDetail) modals.setSelectedSubscriptionDetail(subscriptionDetail)
    cancelPendingModal.open()
  }

  const handleOnSelectTimeInForce = (timeInForce: SubscriptionTimeInForce, ctx?: unknown): void => {
    const { subscription, subscriptionDetail } = (ctx || {}) as TimeInForceContext

    if (!subscriptionDetail) return

    const subscriptionRequestTypeId = getSubscriptionRequestTypeForDetail(subscription)

    if (timeInForce === SubscriptionTimeInForce.BeginningOfNextMonth) {
      onUpdateSubscription(
        {
          subscriptions: [
            {
              subscriptionDetailId: subscriptionDetail.id,
              subscriptionRequestTypeId,
            },
          ],
          subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
        },
        subscriptionDetail
      )
    } else {
      onAddCartItem(subscriptionDetail.id, subscriptionRequestTypeId)
    }

    modals.setSubscriptionTimeInForceType(timeInForce)
  }

  const openTimeInForceModalForDetail = (subscriptionDetail: SubscriptionDetail) => {
    modals.setPreviousSelectedSubscriptionDetail(modals.selectedSubscriptionDetail)
    modals.setSelectedSubscriptionDetail(subscriptionDetail)

    const downgrade = isLevelDowngrade(subscription, subscriptionDetail)

    timeInForceModal.open({
      subscriptionTimeInForceType: modals.subscriptionTimeInForceType,
      levelDowngrade: downgrade,
      context: { subscription, subscriptionDetail } as TimeInForceContext,
      onConfirm: handleOnSelectTimeInForce,
      onCancel: () => {
        if (modals.previousSelectedSubscriptionDetail) {
          modals.setSelectedSubscriptionDetail(modals.previousSelectedSubscriptionDetail)
        }
      },
    } as Partial<SubscriptionTimeInForceModalProps>)
  }

  const handleOnSubscriptionDetailChange = (subscriptionDetail: SubscriptionDetail) => {
    openTimeInForceModalForDetail(subscriptionDetail)
  }

  const render = () => {
    if (subscription.details.length > 1) {
      return (
        <SubscriptionGroupDetail
          handleOnSetConfirmCancelSubscriptionModal={handleOnSetConfirmCancelSubscriptionModal}
          selectedSubscriptionDetail={modals.selectedSubscriptionDetail}
          subscription={subscription}
          handleOnSubscriptionDetailChange={handleOnSubscriptionDetailChange}
          setPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
          nonActiveSubscriptions={nonActiveSubscriptions}
        />
      )
    }

    const [subscriptionDetail] = subscription.details

    if (!subscriptionDetail.subscriptionRequest) {
      return <Body2>{`${formatMoney(subscriptionDetail.amount)}/${t('Subscriptions.mth')}`}</Body2>
    }

    const { type, dueDate, subscriptionTimeInForce } = subscriptionDetail.subscriptionRequest
    const isCancelExistingSubscription =
      type.name === SubscriptionRequestTypeEnum.CancelExistingSubscription

    if (isSubscriptionPending(subscriptionDetail.subscriptionRequest)) {
      const amountPerMonth = `${formatMoney(subscriptionDetail.amount)}/${t('Subscriptions.mth')}`
      if (
        type.name === SubscriptionRequestTypeEnum.AddNewSubscription &&
        subscriptionTimeInForce?.id === SubscriptionTimeInForce.BeginningOfNextMonth
      ) {
        return renderNewSubscriptionPending(amountPerMonth, dueDate, t, dateFormat)
      }

      if (isCancelExistingSubscription) {
        return renderCancelExistingSubscription(
          amountPerMonth,
          dueDate,
          setPendingUnsubscriptionInfoModal,
          t,
          dateFormat
        )
      }
    }

    return <Body2>{`${formatMoney(subscriptionDetail.amount)}/${t('Subscriptions.mth')}`}</Body2>
  }

  return <>{render()}</>
}

export const SubscriptionGroupRenderProductAction = (
  props: SubscriptionGroupSubscriptionProps & ReturnType<typeof useModals>
) => {
  const {
    subscription,
    scrollToCart,
    nonActiveSubscriptions,
    onUpdateSubscription,
    onAddCartItem,
    ...modals
  } = props

  const { t } = useTranslation()

  const activeSubscription = subscription.details.find((d) => d.activeSubscriptionRequest)
  const activeViaBundlePackage = subscription.details.find(
    (d) => d.activeViaBundleSubscriptionRequest
  )

  const shouldHideAction =
    !!activeViaBundlePackage &&
    (!activeSubscription || activeViaBundlePackage?.type.id === activeSubscription.type.id)

  useEffect(() => {
    const subscriptionDetail =
      getActiveSubscriptionLevel(subscription) ||
      getPendingCancelSubscriptionLevel(subscription) ||
      getPendingUpdateSubscriptionLevel(subscription) ||
      getSubscriptionDefaultLevel(subscription) ||
      subscription.details[0]

    modals.setSelectedSubscriptionDetail(subscriptionDetail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription])

  const isSubscriptionInCart = useMemo(
    () => hasLevelInCart(subscription, nonActiveSubscriptions),
    [subscription, nonActiveSubscriptions]
  )

  const attentionModal = useModal(UnsubscribeAttentionModal, {
    onConfirm: () => handleOnUnsubscribeProduct(),
  })
  const confirmationModal = useModal(UnsubscribeConfirmationModal, {
    onConfirm: () => handleOnUnsubscribeProduct(),
  })
  const cancelPendingModal = useModal(ConfirmCancelSubscriptionModal, {
    onConfirm: () => handleOnCancelPendingSubscription(modals.selectedSubscriptionDetail?.id),
  })

  const timeInForceModal = useModal(SubscriptionTimeInForceModal)
  const detailSelectModal = useModal(SubscriptionDetailModal)
  const resubscribeModal = useModal(ResubscribeModal)

  if (!modals.selectedSubscriptionDetail || shouldHideAction) return null

  const { subscriptionRequest } = modals.selectedSubscriptionDetail

  const handleOnCancelPendingSubscription = (subscriptionDetailId?: string) => {
    onUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: subscriptionDetailId || modals.selectedSubscriptionDetail?.id,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
      overrideOpenPositionsForCancel: true,
    })
  }

  const handleOnUnsubscribeProduct = () => {
    onUpdateSubscription(
      {
        subscriptions: [
          {
            subscriptionDetailId:
              modals.selectedSubscriptionDetail?.id ?? subscription?.details[0]?.id,
            subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
          },
        ],
        subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
      },
      modals.selectedSubscriptionDetail
    )
    attentionModal.close()
    confirmationModal.close()
  }

  const handleOnClickUnsubscribe = () => {
    if (isLastBusinessDay()) {
      attentionModal.open()
    } else {
      confirmationModal.open()
    }
  }

  const onHandleOpenResubscribeModal = (subscriptionDetail?: SubscriptionDetail) => {
    const targetDetail = subscriptionDetail ?? modals.selectedSubscriptionDetail
    if (!targetDetail) return

    modals.setSelectedSubscriptionDetail(targetDetail)

    resubscribeModal.open({
      amount: targetDetail.amount,
      onConfirm: () => {
        onUpdateSubscription({
          subscriptions: [
            {
              subscriptionDetailId: targetDetail.id,
              subscriptionRequestTypeId: SubscriptionRequestType.Resubscribe,
            },
          ],
          overrideOpenPositionsForCancel: true,
          subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
        })
      },
    })
  }

  const handleOnSelectTimeInForce = (timeInForce: SubscriptionTimeInForce, ctx?: unknown): void => {
    const { subscription: ctxSubscription, subscriptionDetail } = (ctx || {}) as TimeInForceContext

    if (!ctxSubscription || !subscriptionDetail) return

    const subscriptionRequestTypeId = getSubscriptionRequestTypeForDetail(ctxSubscription)

    if (timeInForce === SubscriptionTimeInForce.BeginningOfNextMonth) {
      onUpdateSubscription(
        {
          subscriptions: [
            {
              subscriptionDetailId: subscriptionDetail.id,
              subscriptionRequestTypeId,
            },
          ],
          subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
        },
        subscriptionDetail
      )
    } else {
      onAddCartItem(subscriptionDetail.id, subscriptionRequestTypeId)
    }

    modals.setSubscriptionTimeInForceType(timeInForce)
  }

  const openTimeInForceModal = (detail: SubscriptionDetail) => {
    modals.setPreviousSelectedSubscriptionDetail(modals.selectedSubscriptionDetail)
    modals.setSelectedSubscriptionDetail(detail)

    const downgrade = isLevelDowngrade(subscription, detail)

    timeInForceModal.open({
      subscriptionTimeInForceType: modals.subscriptionTimeInForceType,
      levelDowngrade: downgrade,
      context: { subscription, subscriptionDetail: detail } as TimeInForceContext,
      onConfirm: handleOnSelectTimeInForce,
      onCancel: () => {
        if (modals.previousSelectedSubscriptionDetail) {
          modals.setSelectedSubscriptionDetail(modals.previousSelectedSubscriptionDetail)
        }
      },
    } as Partial<SubscriptionTimeInForceModalProps>)
  }

  const render = () => {
    switch (true) {
      case isSubscriptionImmediateAndPending(subscription.subscriptionRequest) ||
        isSubscriptionDeductingFundOrInProcess(subscription.subscriptionRequest):
        return (
          <Badge type='yellow' contrast='low' size='sm'>
            {t('Market Data.Processing')}
          </Badge>
        )
      case isCancellationRequested(subscriptionRequest):
        return (
          <Button.Secondary
            size='sm'
            onClick={() => onHandleOpenResubscribeModal(modals.selectedSubscriptionDetail)}
          >
            {t('Subscriptions.Resubscribe')}
          </Button.Secondary>
        )
      case isSubscriptionPending(subscription.subscriptionRequest): {
        const pendingSubscriptionDetail = subscription.details.find(
          (d) => d.subscriptionRequest && isSubscriptionPending(d.subscriptionRequest)
        )
        return (
          <Button.Secondary
            size='sm'
            onClick={() => {
              if (pendingSubscriptionDetail) {
                modals.setSelectedSubscriptionDetail(pendingSubscriptionDetail)
              }
              cancelPendingModal.open()
            }}
          >
            {t('Cancel')}
          </Button.Secondary>
        )
      }
      case isSubscriptionActive(modals.selectedSubscriptionDetail):
        return (
          <Button.Secondary size='sm' onClick={handleOnClickUnsubscribe}>
            {t('Subscriptions.Unsubscribe')}
          </Button.Secondary>
        )
      case isSubscriptionPending(subscriptionRequest):
        return (
          <CancelOrProcessingButton
            subscriptionRequest={subscriptionRequest}
            scrollToCart={scrollToCart}
            onCancel={() => cancelPendingModal.open()}
          />
        )
      case isSubscriptionDeductingFundOrInProcess(subscriptionRequest):
        return (
          <Badge type='yellow' contrast='low' size='sm'>
            {t('Subscriptions.Processing')}
          </Badge>
        )
      default:
        return (
          <Button.Primary
            size='sm'
            disabled={isSubscriptionInCart}
            onClick={() => {
              const currentDetail = modals.selectedSubscriptionDetail
              if (!currentDetail) return

              if (subscription.details.length > 1) {
                return detailSelectModal.open({
                  subscription,
                  selectedSubscriptionDetail:
                    subscription.details.find((d) => !!d.activeViaBundleSubscriptionRequest) ??
                    currentDetail,
                  isSelectEnabled: true,
                  onConfirm: (detail) => {
                    if (!detail) return
                    openTimeInForceModal(detail)
                  },
                })
              }

              openTimeInForceModal(currentDetail)
            }}
          >
            {t('Market Data.Subscribe')}
          </Button.Primary>
        )
    }
  }

  return <>{render()}</>
}

const getSubscriptionRequestTypeForDetail = (subscription: Subscription): SubscriptionRequestType =>
  subscription.activeSubscriptionRequest ||
  (subscription.subscriptionRequest &&
    subscription.subscriptionRequest.state.name === SubscriptionRequestState.Pending)
    ? SubscriptionRequestType.Update
    : SubscriptionRequestType.Add
